import { useCallback, useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { useDistributorWorkspace } from '@/contexts/DistributorWorkspaceContext'
import {
  fetchMyDistributorPayments,
  formatMyDistributorCurrency,
} from '@/lib/my-distributor'
import { RequireDistributorWorkspace } from '@/pages/SouDistribuidorPage/RequireDistributorWorkspace'
import type { MyDistributorPaymentItem } from '@/types/my-distributor'
import styles from './SouDistribuidorPage.module.css'

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function SouDistribuidorAfiliadosVendasPageContent() {
  const { selectedProgram, campaignId } = useDistributorWorkspace()
  const [items, setItems] = useState<MyDistributorPaymentItem[]>([])
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState<{
    current_page: number
    last_page: number
    total: number
  } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadPayments = useCallback(
    async (p: number) => {
      if (!selectedProgram) return
      setIsLoading(true)
      setError(null)
      try {
        const res = await fetchMyDistributorPayments(selectedProgram.distributor_id, {
          campaign_id: campaignId ?? undefined,
          page: p,
          per_page: 20,
        })
        setItems(res.items)
        setPagination({
          current_page: res.pagination.current_page,
          last_page: res.pagination.last_page,
          total: res.pagination.total,
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar vendas.')
      } finally {
        setIsLoading(false)
      }
    },
    [selectedProgram, campaignId]
  )

  useEffect(() => {
    setPage(1)
  }, [selectedProgram?.distributor_id, campaignId])

  useEffect(() => {
    if (!selectedProgram) {
      setIsLoading(false)
      return
    }
    void loadPayments(page)
  }, [selectedProgram, page, loadPayments])

  if (error) {
    return (
      <div className={styles.centered}>
        <span>{error}</span>
        <button type="button" className={styles.retryBtn} onClick={() => void loadPayments(page)}>
          Tentar novamente
        </button>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:loading" className={styles.spinner} />
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className={styles.emptyBlock}>
        <Icon icon="mdi:receipt-text-remove-outline" />
        <span>Nenhuma venda paga atribuída neste contexto.</span>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Campanha</th>
              <th>Comprador</th>
              <th>Afiliado</th>
              <th>Valor</th>
              <th>Com. distribuidor</th>
              <th>Com. afiliado</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.payment_id}>
                <td>{formatDate(item.paid_at)}</td>
                <td>{item.campaign.title?.trim() || item.campaign.name}</td>
                <td>{item.buyer.name}</td>
                <td>{item.affiliate?.name ?? '—'}</td>
                <td>{formatMyDistributorCurrency(item.payment_amount)}</td>
                <td>{formatMyDistributorCurrency(item.distributor_commission_amount)}</td>
                <td>
                  {item.affiliate
                    ? formatMyDistributorCurrency(item.affiliate_commission_amount)
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && pagination.last_page > 1 ? (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span>
            Página {pagination.current_page} de {pagination.last_page} ({pagination.total} vendas)
          </span>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={page >= pagination.last_page}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </button>
        </div>
      ) : null}
    </div>
  )
}

export function SouDistribuidorAfiliadosVendasPage() {
  return (
    <RequireDistributorWorkspace requireActiveProgram>
      <SouDistribuidorAfiliadosVendasPageContent />
    </RequireDistributorWorkspace>
  )
}
